import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, Mail, Phone, Clock, Linkedin, Twitter, Facebook, Instagram, MessageCircle, ArrowUpRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Logo } from "@/components/Logo";
import { PillButton } from "@/components/PillButton";
import { useSettings } from "@/hooks/useSiteData";

const NAV = [
  { to: "/", label: "Home", end: true },
  { to: "/services", label: "Services" },
  { to: "/products", label: "Products" },
  { to: "/industries", label: "Industries" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const SOCIAL_ICONS = { linkedin: Linkedin, twitter: Twitter, facebook: Facebook, instagram: Instagram };

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { data: settings } = useSettings();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => setOpen(false), [location.pathname]);

  const wa = settings?.whatsapp ? `https://wa.me/${settings.whatsapp}?text=${encodeURIComponent("Hello Jonaren Technology, I would like to discuss a project.")}` : null;
  const socials = Object.entries(settings?.social || {}).filter(([, v]) => v);
  const phone = settings?.phones?.[0];

  return (
    <>
      {/* Utility bar */}
      <div className="ink hidden text-[13px] lg:block" data-testid="utility-bar">
        <div className="container-x flex h-10 items-center justify-between text-white/75">
          <div className="flex items-center gap-6">
            {settings?.emails?.[0] && <a href={`mailto:${settings.emails[0]}`} className="inline-flex items-center gap-2 transition-colors hover:text-white" data-testid="utility-email"><Mail className="h-3.5 w-3.5 text-primary" />{settings.emails[0]}</a>}
            {phone && <a href={`tel:${phone.replace(/\s+/g, "")}`} className="inline-flex items-center gap-2 transition-colors hover:text-white" data-testid="utility-phone"><Phone className="h-3.5 w-3.5 text-primary" />{phone}</a>}
          </div>
          <div className="flex items-center gap-6">
            {settings?.working_hours && <span className="inline-flex items-center gap-2"><Clock className="h-3.5 w-3.5 text-primary" />{settings.working_hours}</span>}
            {socials.length > 0 && (
              <span className="flex items-center gap-3 border-l border-white/15 pl-6">
                {socials.map(([k, href]) => { const I = SOCIAL_ICONS[k]; return I ? <a key={k} href={href} target="_blank" rel="noopener noreferrer" aria-label={k} className="transition-colors hover:text-white"><I className="h-3.5 w-3.5" /></a> : null; })}
              </span>
            )}
          </div>
        </div>
      </div>

      <header className={`sticky top-0 z-50 border-b bg-white transition-[box-shadow,border-color] duration-200 ${scrolled ? "border-border shadow-[0_8px_30px_-18px_rgba(14,16,19,0.35)]" : "border-border/60"}`} data-testid="site-header">
        <div className="container-x flex h-[72px] items-center justify-between gap-4 lg:h-20">
          <Logo className="h-[44px] sm:h-[49px]" />

          <nav className="hidden items-center lg:flex" aria-label="Primary">
            {NAV.map((n) => (
              <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`} data-testid={`nav-link-${n.label.toLowerCase()}`}>{n.label}</NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-3 lg:flex">
            {wa && (
              <a href={wa} target="_blank" rel="noopener noreferrer" aria-label="Chat on WhatsApp" className="inline-flex h-12 w-12 items-center justify-center rounded-full border border-border text-foreground transition-colors hover:border-foreground" data-testid="header-whatsapp-button"><MessageCircle className="h-5 w-5" /></a>
            )}
            <PillButton to="/contact" variant="dark" testId="header-primary-cta-button">Get a Free Consultation</PillButton>
          </div>

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="h-11 w-11 rounded-full bg-white lg:hidden" aria-label="Open menu" data-testid="mobile-menu-button"><Menu className="h-5 w-5" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[88vw] max-w-sm bg-white p-0" data-testid="mobile-menu-drawer">
              <SheetTitle className="sr-only">Navigation menu</SheetTitle>
              <div className="border-b border-border p-5"><Logo className="h-8" testId="mobile-site-logo" /></div>
              <nav className="flex flex-col p-3" aria-label="Mobile">
                {NAV.map((n) => (
                  <NavLink key={n.to} to={n.to} end={n.end} className={({ isActive }) => `flex items-center justify-between rounded-xl px-4 py-3.5 font-heading text-lg font-medium transition-colors ${isActive ? "bg-secondary text-foreground" : "text-foreground/70 hover:bg-secondary hover:text-foreground"}`} data-testid={`mobile-nav-link-${n.label.toLowerCase()}`}>{n.label}<ArrowUpRight className="h-4 w-4 text-primary" /></NavLink>
                ))}
              </nav>
              <div className="mt-2 flex flex-col gap-3 p-5">
                <PillButton to="/contact" variant="primary" className="justify-between" testId="mobile-primary-cta-button">Get a Free Consultation</PillButton>
                {wa && <PillButton href={wa} variant="outline" icon={MessageCircle} className="justify-between" testId="mobile-whatsapp-button">WhatsApp us</PillButton>}
                <div className="mt-4 space-y-2 text-sm text-muted-foreground">
                  {settings?.emails?.[0] && <p className="flex items-center gap-2"><Mail className="h-4 w-4 text-primary" />{settings.emails[0]}</p>}
                  {phone && <p className="flex items-center gap-2"><Phone className="h-4 w-4 text-primary" />{phone}</p>}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </header>
    </>
  );
};
